/**
 * Composant principal de l'application PathBlock
 */

import React, { useMemo } from 'react';
import { useSocket } from './hooks/useSocket';
import { useGameStore } from './store/gameStore';
import { useKeyboard } from './hooks/useKeyboard';
import { Lobby } from './components/Lobby';
import { Board } from './components/Board';
import { PlayerInfo } from './components/PlayerInfo';
import { GameStatus } from './components/GameStatus';
import { findPathToCell } from '@pathblock/core';

const App: React.FC = () => {
  useSocket();
  useKeyboard();

  const view = useGameStore((state) => state.view);
  const gameState = useGameStore((state) => state.gameState);
  const playerId = useGameStore((state) => state.playerId);
  const remainingTime = useGameStore((state) => state.remainingTime);
  const opponentDisconnected = useGameStore((state) => state.opponentDisconnected);
  const connected = useGameStore((state) => state.connected);
  const isVsAI = useGameStore((state) => state.isVsAI);

  // Distance au but pour chaque joueur (BFS côté client)
  const p1Distance = useMemo(() => {
    if (!gameState || gameState.phase !== 'playing') return null;
    const p = gameState.players[0];
    const path = findPathToCell(p.position, p.targetRow, p.targetCol, gameState.walls);
    return path ? path.length - 1 : null;
  }, [gameState]);

  const p2Distance = useMemo(() => {
    if (!gameState || gameState.phase !== 'playing') return null;
    const p = gameState.players[1];
    const path = findPathToCell(p.position, p.targetRow, p.targetCol, gameState.walls);
    return path ? path.length - 1 : null;
  }, [gameState]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-400' : 'bg-red-400 animate-pulse'}`} />
            <span className="text-xs text-gray-400">{connected ? 'Connecté' : 'Reconnexion...'}</span>
          </div>
          <h1 className="text-2xl font-bold">
            <span className="text-blue-400">Path</span>
            <span className="text-orange-400">Block</span>
          </h1>
          <div className="text-xs text-gray-400 text-right">
            {isVsAI && <span className="text-purple-400">vs IA</span>}
            {!isVsAI && (view === 'playing' || view === 'finished') && (
              <span>Tour {gameState?.turnNumber ?? 0}</span>
            )}
          </div>
        </div>
      </header>

      {/* Bandeau reconnexion adversaire */}
      {opponentDisconnected && (
        <div className="bg-yellow-500/90 text-yellow-900 text-center py-2 px-4 text-sm font-medium">
          Adversaire déconnecté — 30 secondes pour revenir...
        </div>
      )}

      {/* Contenu principal */}
      <main className="container mx-auto px-4 py-8">
        {(view === 'lobby' || view === 'waiting') && <Lobby />}

        {(view === 'playing' || view === 'finished') && gameState && (
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 items-start">
              {/* Info Joueur 1 */}
              <div className="order-2 lg:order-1">
                <PlayerInfo
                  player={gameState.players[0]}
                  isCurrentPlayer={gameState.currentPlayer === 1}
                  isMe={playerId === 1}
                  remainingTime={remainingTime}
                  turnTimeLimit={gameState.turnTimeLimit}
                  distanceToGoal={p1Distance}
                />
              </div>

              {/* Plateau de jeu */}
              <div className="order-1 lg:order-2 flex flex-col items-center gap-6">
                <Board />
                <GameStatus />
              </div>

              {/* Info Joueur 2 */}
              <div className="order-3">
                <PlayerInfo
                  player={gameState.players[1]}
                  isCurrentPlayer={gameState.currentPlayer === 2}
                  isMe={playerId === 2}
                  remainingTime={remainingTime}
                  turnTimeLimit={gameState.turnTimeLimit}
                  distanceToGoal={p2Distance}
                />
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 bg-gray-800/80 backdrop-blur border-t border-gray-700">
        <div className="container mx-auto px-4 py-2">
          <p className="text-center text-gray-500 text-sm">
            PathBlock - Jeu de stratégie inspiré de Quoridor
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
